import invariant from 'tiny-invariant'

import {
  API_PATH_INTERNAL,
  DEFAULT_BULK_IMPORT_CIRCUMSTANCES,
  DEFAULT_BULK_IMPORT_REASON,
  REFERRAL_TYPE,
} from '@/lib/constants'
import { ReferralsImportItem } from '@/redux/slices/referralsImportBase'
import {
  CompanyEntity,
  ReferralStatusEntity,
  ServiceAreaEntity,
  UserEntity,
} from '@/types/entities'
import { ReferralSource } from '@/types/misc'
import { CreateHistoricalReferralRequest } from '@/types/requests/referrals'

export function buildCreateHistoricalReferralRequest(
  importItem: Pick<
    ReferralsImportItem,
    | 'circumstances'
    | 'fromAdviserEmail'
    | 'hasPausedClientEmails'
    | 'importingSource'
    | 'importingCompanyClient'
    | 'inviteFromAdviser'
    | 'inviteToAdviser'
    | 'isFromInvite'
    | 'isToInvite'
    | 'multipleFromInvites'
    | 'multipleToInvites'
    | 'referralDate'
    | 'toAdviserEmail'
  > & {
    toCompany: Pick<CompanyEntity, 'id'> | null
    toCompanyUser: Pick<UserEntity, 'id'> | null
    serviceArea: Pick<ServiceAreaEntity, 'id'> | null
    fromCompany: Pick<CompanyEntity, 'id'> | null
    fromCompanyUser: Pick<UserEntity, 'id'> | null
    status: Pick<ReferralStatusEntity, 'id'> | null
    isLoggedReferral: boolean
    source: ReferralSource
    directLeadSource?: string
  },
): CreateHistoricalReferralRequest {
  invariant(
    importItem?.importingCompanyClient?.id,
    'Expected client to be created',
  )
  invariant(
    !(importItem.isFromInvite && importItem.isToInvite),
    'Cannot be a both side invite',
  )
  invariant(importItem.serviceArea, 'Expected service area to be set')

  // @todo: Add a type for commonFields
  const commonFields = {
    circumstances:
      importItem.circumstances ?? DEFAULT_BULK_IMPORT_CIRCUMSTANCES,
    reason: DEFAULT_BULK_IMPORT_REASON,
    type: REFERRAL_TYPE.indirect, // Direct will often be blocked by logic set out in API getAvailableReferralTypes()
    serviceAreaIds: [importItem.serviceArea?.id ?? 0],
    hasClientConsent: false,
    isImport: true,
    isLoggedReferral: importItem.isLoggedReferral,
    isHistoricalReferral: true as const,
    importMappedStatus: importItem.status
      ? `${API_PATH_INTERNAL}/referral-mapped-statuses/${importItem.status.id}`
      : undefined,
    referralDate: importItem.referralDate ?? undefined,
    hasPausedClientEmails: importItem.hasPausedClientEmails,
    source: importItem.source,
    directLeadSource: importItem.directLeadSource,
  }

  if (importItem.isFromInvite) {
    // invites for fromAdviserEmail
    invariant(importItem.fromAdviserEmail, 'Expected advisor email to be set')
    invariant(importItem.importingSource === 'TO', 'Expected client to be set')
    invariant(importItem.toCompany, 'Expected toCompany to be set')

    return {
      ...commonFields,
      fromCompany: undefined,
      fromClient: undefined,
      toCompany: `${API_PATH_INTERNAL}/companies/${importItem.toCompany.id}`,
      toCompanyUsers: importItem.toCompanyUser
        ? [`${API_PATH_INTERNAL}/users/${importItem.toCompanyUser.id}`]
        : [],
      toClient: importItem.importingCompanyClient['@id'],
      referralInvites: [
        {
          email: importItem.fromAdviserEmail,
        },
      ],
      skipInitialAdvisorInvite: !importItem.inviteFromAdviser,
      asMultipleInvites: importItem.multipleFromInvites,
    }
  } else if (importItem.isToInvite) {
    // invites for toAdviserEmail
    invariant(importItem.toAdviserEmail, 'Expected advisor email to be set')
    invariant(
      importItem.importingSource === 'FROM',
      'Expected client to be set',
    )
    invariant(importItem.fromCompany, 'Expected fromCompany to be set')

    return {
      ...commonFields,
      fromCompany: `${API_PATH_INTERNAL}/companies/${importItem.fromCompany.id}`,
      fromCompanyUserOverwrite: importItem.fromCompanyUser
        ? `${API_PATH_INTERNAL}/users/${importItem.fromCompanyUser.id}`
        : undefined,
      fromClient: importItem.importingCompanyClient['@id'],
      toCompany: undefined,
      toClient: undefined,
      toCompanyUsers: [],
      referralInvites: [
        {
          email: importItem.toAdviserEmail,
        },
      ],
      skipInitialAdvisorInvite: !importItem.inviteToAdviser,
      asMultipleInvites: importItem.multipleToInvites,
    }
  } else {
    // regular referrals
    invariant(importItem.fromCompany, 'Expected fromCompany to be set')
    invariant(importItem.toCompany, 'Expected toCompany to be set')

    return {
      ...commonFields,
      fromCompany: `${API_PATH_INTERNAL}/companies/${importItem.fromCompany.id}`,
      fromCompanyUserOverwrite: importItem.fromCompanyUser
        ? `${API_PATH_INTERNAL}/users/${importItem.fromCompanyUser.id}`
        : undefined,
      fromClient:
        importItem.importingSource === 'FROM'
          ? importItem.importingCompanyClient['@id']
          : undefined,
      toCompany: `${API_PATH_INTERNAL}/companies/${importItem.toCompany.id}`,
      toCompanyUsers: importItem.toCompanyUser
        ? [`${API_PATH_INTERNAL}/users/${importItem.toCompanyUser.id}`]
        : [],
      toClient:
        importItem.importingSource === 'TO'
          ? importItem.importingCompanyClient['@id']
          : undefined,
      referralInvites: [],
    }
  }
}
